import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ArtSubmissionForm = () => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [formData, setFormData] = useState({
    artistName: '',
    email: '',
    artTitle: '',
    artType: '',
    description: '',
    inspiration: '',
    file: null,
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('memorial-language') || 'en';
    setCurrentLanguage(savedLanguage);

    // Listen for language changes
    const handleLanguageChange = (event) => {
      const newLanguage = event.detail?.language || 'en';
      setCurrentLanguage(newLanguage);
    };

    window.addEventListener('languageChanged', handleLanguageChange);
    return () => window.removeEventListener('languageChanged', handleLanguageChange);
  }, []);

  const formContent = {
    ar: {
      title: "تقديم الأعمال الفنية",
      description: "شارك إبداعك الفني تكريماً للذكرى",
      artistNameLabel: "اسم الفنان",
      artistNamePlaceholder: "أدخل اسمك الفني أو الحقيقي",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "أدخل بريدك الإلكتروني",
      artTitleLabel: "عنوان العمل الفني",
      artTitlePlaceholder: "أدخل عنوان عملك الفني",
      artTypeLabel: "نوع العمل الفني",
      artTypePlaceholder: "اختر نوع عملك الفني",
      descriptionLabel: "وصف العمل",
      descriptionPlaceholder: "اكتب وصفاً لعملك الفني...",
      inspirationLabel: "مصدر الإلهام",
      inspirationPlaceholder: "ما الذي ألهمك لإنشاء هذا العمل؟",
      fileLabel: "رفع العمل الفني",
      fileDescription: "اسحب وأفلت ملفك هنا أو انقر للاختيار",
      fileFormats: "PNG, JPG, PDF حتى 10MB",
      submitButton: "إرسال العمل الفني",
      submittingButton: "جاري الإرسال...",
      successTitle: "تم استلام عملك الفني!",
      successMessage: "شكراً لك على مشاركة إبداعك. سيتم مراجعة عملك الفني وإضافته إلى المعرض.",
      submissionCount: "عمل فني",
      requiredError: "هذا الحقل مطلوب",
      emailError: "يرجى إدخال بريد إلكتروني صحيح",
      fileError: "يرجى رفع ملف صالح",
      fileSizeError: "حجم الملف يجب أن يكون أقل من 10MB",
      phoneLabel: "رقم الهاتف",
      phonePlaceholder: "أدخل رقم هاتفك",
      phoneError: "يرجى إدخال رقم هاتف صحيح",
      phoneLimitError: "تم استخدام هذا الرقم 3 مرات بالفعل. الحد الأقصى المسموح به هو 3 إرسالات.",
      submitError: "حدث خطأ أثناء إرسال الطلب. يرجى المحاولة مرة أخرى."
    },
    en: {
      title: "Art Submission",
      description: "Share your artistic creativity in honor of the memorial",
      artistNameLabel: "Artist Name",
      artistNamePlaceholder: "Enter your artistic or real name",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email address",
      artTitleLabel: "Artwork Title",
      artTitlePlaceholder: "Enter the title of your artwork",
      artTypeLabel: "Art Type",
      artTypePlaceholder: "Select your artwork type",
      descriptionLabel: "Artwork Description",
      descriptionPlaceholder: "Write a description of your artwork...",
      inspirationLabel: "Source of Inspiration",
      inspirationPlaceholder: "What inspired you to create this work?",
      fileLabel: "Upload Artwork",
      fileDescription: "Drag and drop your file here or click to select",
      fileFormats: "PNG, JPG, PDF up to 10MB",
      submitButton: "Submit Artwork",
      submittingButton: "Submitting...",
      successTitle: "Your Artwork Received!",
      successMessage: "Thank you for sharing your creativity. Your artwork will be reviewed and added to the gallery.",
      submissionCount: "artworks",
      requiredError: "This field is required",
      emailError: "Please enter a valid email address",
      fileError: "Please upload a valid file",
      fileSizeError: "File size must be less than 10MB",
      phoneLabel: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      phoneError: "Please enter a valid phone number",
      phoneLimitError: "This phone number has already been used 3 times. Maximum allowed is 3 submissions.",
      submitError: "Failed to submit. Please try again."
    },
    tr: {
      title: "Sanat Eseri Gönderimi",
      description: "Anıt onuruna sanatsal yaratıcılığınızı paylaşın",
      artistNameLabel: "Sanatçı Adı",
      artistNamePlaceholder: "Sanatçı adınızı veya gerçek adınızı girin",
      emailLabel: "E-posta Adresi",
      emailPlaceholder: "E-posta adresinizi girin",
      artTitleLabel: "Sanat Eseri Başlığı",
      artTitlePlaceholder: "Sanat eserinizin başlığını girin",
      artTypeLabel: "Sanat Türü",
      artTypePlaceholder: "Sanat eserinizin türünü seçin",
      descriptionLabel: "Sanat Eseri Açıklaması",
      descriptionPlaceholder: "Sanat eserinizin açıklamasını yazın...",
      inspirationLabel: "İlham Kaynağı",
      inspirationPlaceholder: "Bu eseri yaratmanıza ne ilham verdi?",
      fileLabel: "Sanat Eseri Yükle",
      fileDescription: "Dosyanızı buraya sürükleyip bırakın veya seçmek için tıklayın",
      fileFormats: "PNG, JPG, PDF 10MB'a kadar",
      submitButton: "Sanat Eseri Gönder",
      submittingButton: "Gönderiliyor...",
      successTitle: "Sanat Eseriniz Alındı!",
      successMessage: "Yaratıcılığınızı paylaştığınız için teşekkürler. Sanat eseriniz incelenecek ve galeriye eklenecek.",
      submissionCount: "sanat eseri",
      requiredError: "Bu alan gereklidir",
      emailError: "Lütfen geçerli bir e-posta adresi girin",
      fileError: "Lütfen geçerli bir dosya yükleyin",
      fileSizeError: "Dosya boyutu 10MB\'dan küçük olmalıdır",
      phoneLabel: "Telefon Numarası",
      phonePlaceholder: "Telefon numaranızı girin",
      phoneError: "Lütfen geçerli bir telefon numarası girin",
      phoneLimitError: "Bu telefon numarası zaten 3 kez kullanıldı. Maksimum izin verilen 3 gönderimdir.",
      submitError: "Gönderim başarısız oldu. Lütfen tekrar deneyin."
    }
  };

  const artTypeOptions = [
    { 
      value: 'painting', 
      label: currentLanguage === 'ar' ? 'لوحة فنية' : 
             currentLanguage === 'tr' ? 'Resim' : 'Painting' 
    },
    { 
      value: 'drawing', 
      label: currentLanguage === 'ar' ? 'رسم' : 
             currentLanguage === 'tr' ? 'Çizim' : 'Drawing' 
    },
    { 
      value: 'photography', 
      label: currentLanguage === 'ar' ? 'تصوير فوتوغرافي' : 
             currentLanguage === 'tr' ? 'Fotoğraf' : 'Photography' 
    },
    { 
      value: 'digital_art', 
      label: currentLanguage === 'ar' ? 'فن رقمي' : 
             currentLanguage === 'tr' ? 'Dijital sanat' : 'Digital Art' 
    },
    { 
      value: 'sculpture', 
      label: currentLanguage === 'ar' ? 'نحت' : 
             currentLanguage === 'tr' ? 'Heykel' : 'Sculpture' 
    },
    { 
      value: 'calligraphy', 
      label: currentLanguage === 'ar' ? 'خط عربي' : 
             currentLanguage === 'tr' ? 'Kaligrafi' : 'Calligraphy' 
    },
    { 
      value: 'mixed_media', 
      label: currentLanguage === 'ar' ? 'وسائط مختلطة' : 
             currentLanguage === 'tr' ? 'Karma medya' : 'Mixed Media' 
    },
    { 
      value: 'other', 
      label: currentLanguage === 'ar' ? 'أخرى' : 
             currentLanguage === 'tr' ? 'Diğer' : 'Other' 
    }
  ];

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFileChange(e?.dataTransfer?.files?.[0]);
    }
  };

  const content = formContent?.[currentLanguage];

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData?.artistName?.trim()) {
      newErrors.artistName = content?.requiredError;
    }
    
    if (!formData?.email?.trim()) {
      newErrors.email = content?.requiredError;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = content?.emailError;
    }
    
    if (!formData?.artTitle?.trim()) {
      newErrors.artTitle = content?.requiredError;
    }
    
    if (!formData?.artType) {
      newErrors.artType = content?.requiredError;
    }
    
    if (!formData?.description?.trim()) {
      newErrors.description = content?.requiredError;
    }
    
    if (!formData?.file) {
      newErrors.file = content?.fileError;
    }
    
    if (!formData?.phone?.trim()) {
      newErrors.phone = content?.requiredError;
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = content?.phoneError;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setErrors({}); // Clear previous errors
    
    try {
      let fileBase64 = null;
      let fileMimeType = null;
      
      // Convert file to base64 if file exists
      if (formData.file) {
        fileBase64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(formData.file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = error => reject(error);
        });
        fileMimeType = formData.file.type;
      }

      // Get API token from environment variable
      const apiToken = import.meta.env.VITE_API_SECRET_TOKEN || '';
      
      // Prepare submission data
      const submissionData = {
        formType: 'art',
        artistName: formData.artistName,
        email: formData.email,
        artTitle: formData.artTitle,
        artType: formData.artType,
        description: formData.description,
        inspiration: formData.inspiration || '',
        fileName: formData.file ? formData.file.name : '',
        fileBase64: fileBase64,
        fileMimeType: fileMimeType,
        phone: formData.phone || '',
        token: apiToken, // Include API token for authentication
        origin: window.location.origin // Include origin for CORS validation
      };

      // Get Google Script URL from environment variable
      const scriptUrl = import.meta.env.VITE_GOOGLE_SCRIPT_URL || '';
      
      if (!scriptUrl) {
        throw new Error('Google Script URL not configured');
      }
      
      // Send to Google Apps Script
      // CORS is required - no fallback to no-cors mode
      const response = await fetch(scriptUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData)
      });

      // Parse response
      const result = await response.json();
      
      // Check if submission was successful
      if (!result.success) {
        throw new Error(result.error || 'Failed to submit. Please try again.');
      }

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after success
      setTimeout(() => {
        setIsSubmitted(false);
          setFormData({
            artistName: '',
            email: '',
            artTitle: '',
            artType: '',
            description: '',
            inspiration: '',
            file: null,
            phone: ''
          });
      }, 3000);
      
    } catch (error) {
      console.error('Failed to submit:', error);
      setIsSubmitting(false);
      
      const errorMessage = error.message || 
                          content?.submitError || 
                          'Failed to submit. Please try again.';
      
      // Check if it's a phone limit error
      if (error.message && (error.message.includes('phone number') || error.message.includes('limit exceeded') || error.message.includes('already been used'))) {
        setErrors({ 
          phone: content?.phoneLimitError || errorMessage
        });
      } else {
        setErrors({ 
          submit: errorMessage
        });
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleFileChange = (file) => {
    if (file) {
      if (file?.size > 10 * 1024 * 1024) { // 10MB
        setErrors(prev => ({ ...prev, file: content?.fileSizeError }));
        return;
      }
      
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf'];
      if (!allowedTypes?.includes(file?.type)) {
        setErrors(prev => ({ ...prev, file: content?.fileError }));
        return;
      }
      
      setFormData(prev => ({ ...prev, file }));
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center py-8"
      >
        <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <Icon name="Palette" size={32} className="text-success" />
        </div>
        <h3 className="font-body text-xl font-semibold text-primary mb-2">
          {content?.successTitle}
        </h3>
        <p className="font-body text-text-secondary mb-6">
          {content?.successMessage}
        </p>
        <div className="bg-success/5 border border-success/20 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-3">
            <Icon name="Image" size={20} className="text-success" />
            <span className="font-body text-sm text-success font-medium">
              89 {content?.submissionCount}
            </span>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <div>
      <div className="text-center mb-8">
        <h3 className="font-body text-2xl font-semibold text-primary mb-2">
          {content?.title}
        </h3>
        <p className="font-body text-text-secondary">
          {content?.description}
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.artistNameLabel}
            type="text"
            placeholder={content?.artistNamePlaceholder}
            value={formData?.artistName}
            onChange={(e) => handleInputChange('artistName', e?.target?.value)}
            error={errors?.artistName}
            required
          />

          <Input
            label={content?.emailLabel}
            type="email"
            placeholder={content?.emailPlaceholder}
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.phoneLabel}
            type="number"
            placeholder={content?.phonePlaceholder}
            value={formData?.phone}
            onChange={(e) => handleInputChange('phone', e?.target?.value)}
            error={errors?.phone}
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label={content?.artTitleLabel}
            type="text"
            placeholder={content?.artTitlePlaceholder}
            value={formData?.artTitle}
            onChange={(e) => handleInputChange('artTitle', e?.target?.value)}
            error={errors?.artTitle}
            required
          />

          <Select
            label={content?.artTypeLabel}
            placeholder={content?.artTypePlaceholder}
            options={artTypeOptions}
            value={formData?.artType}
            onChange={(value) => handleInputChange('artType', value)}
            error={errors?.artType}
            required
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block font-body text-sm font-medium text-text-primary mb-2">
            {content?.fileLabel} *
          </label>
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 memorial-transition ${
              dragActive 
                ? 'border-primary bg-primary/5' 
                : errors?.file 
                ? 'border-error bg-error/5' :'border-border hover:border-primary hover:bg-muted/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => handleFileChange(e?.target?.files?.[0])}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
            
            <div className="text-center">
              <Icon 
                name={formData?.file ? "CheckCircle" : "Upload"} 
                size={32} 
                className={`mx-auto mb-4 ${
                  formData?.file ? 'text-success' : 'text-text-secondary'
                }`} 
              />
              
              {formData?.file ? (
                <div>
                  <p className="font-body text-sm font-medium text-success mb-1">
                    {formData?.file?.name}
                  </p>
                  <p className="font-body text-xs text-text-secondary">
                    {(formData?.file?.size / 1024 / 1024)?.toFixed(2)} MB
                  </p>
                </div>
              ) : (
                <div>
                  <p className="font-body text-sm text-text-primary mb-1">
                    {content?.fileDescription}
                  </p>
                  <p className="font-body text-xs text-text-secondary">
                    {content?.fileFormats}
                  </p>
                </div>
              )}
            </div>
          </div>
          {errors?.file && (
            <p className="mt-1 text-sm text-error">{errors?.file}</p>
          )}
        </div>

        <div>
          <label className="block font-body text-sm font-medium text-text-primary mb-2">
            {content?.descriptionLabel} *
          </label>
          <textarea
            placeholder={content?.descriptionPlaceholder}
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            rows={4}
            className={`w-full px-3 py-2 border-2 rounded-md font-body text-sm memorial-transition focus:outline-none focus:border-primary ${
              errors?.description 
                ? 'border-error text-error placeholder-error/50 focus:border-error' :'border-border text-text-primary placeholder-text-secondary'
            }`}
          />
          {errors?.description && (
            <p className="mt-1 text-sm text-error">{errors?.description}</p>
          )}
        </div>

        <div>
          <label className="block font-body text-sm font-medium text-text-primary mb-2">
            {content?.inspirationLabel}
          </label>
          <textarea
            placeholder={content?.inspirationPlaceholder}
            value={formData?.inspiration}
            onChange={(e) => handleInputChange('inspiration', e?.target?.value)}
            rows={3}
            className="w-full px-3 py-2 border-2 border-border rounded-md font-body text-sm memorial-transition focus:outline-none focus:border-primary text-text-primary placeholder-text-secondary"
          />
        </div>

        {errors?.submit && (
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <p className="font-body text-sm text-error text-center">
              {errors.submit}
            </p>
          </div>
        )}

        <Button
          type="submit"
          variant="default"
          size="lg"
          loading={isSubmitting}
          disabled={isSubmitting}
          iconName="Send"
          iconPosition="left"
          fullWidth
        >
          {isSubmitting ? content?.submittingButton : content?.submitButton}
        </Button>
      </form>
      {/* Submission Counter */}
      <div className="mt-8 text-center">
        <div className="inline-flex items-center space-x-3 bg-muted rounded-lg px-4 py-2">
          <Icon name="Image" size={16} className="text-primary" />
          <span className="font-body text-sm text-text-secondary">
            89 {content?.submissionCount}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ArtSubmissionForm;